import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useProtectedClient } from 'hooks';
import { useField, useFormikContext } from 'formik';
import { ContentEmailValues } from 'features/admin/types';

interface Props {
  edit?: boolean;
}

const useStyles = makeStyles(theme => ({
  spacer: {
    marginTop: theme.spacing(3),
  },
}));

const FunctionTemplate: FC<Props> = ({ edit }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { email_from_address, email_sending_domain } = useProtectedClient();
  const {
    values: { email_from, email_reply_to },
  } = useFormikContext<ContentEmailValues>();

  const [, emailFromMeta, emailFromHelpers] = useField('email_from');
  const [, emailReplyToMeta, emailReplyToHelpers] = useField('email_reply_to');
  const nameToUse = edit ? email_from?.split('@')[0] : email_from_address?.split('@')?.[0] ?? '';
  const sending_domain = edit ? email_from?.split('@')[1] : email_sending_domain;

  const [fromName, setFromName] = useState(nameToUse);
  const [replyToName, setReplyToName] = useState(edit ? email_reply_to : email_from_address);

  const hasFromError = !!emailFromMeta.error && !!emailFromMeta.touched;
  const fromErrorText = hasFromError ? { helperText: emailFromMeta.error } : {};

  const hasReplyToError = !!emailReplyToMeta.error && !!emailReplyToMeta.touched;
  const replyToErrorText = hasReplyToError ? { helperText: emailReplyToMeta.error } : {};

  const endAdornment = (
    <Box mr={2}>
      <Typography>{`@${sending_domain}`}</Typography>
    </Box>
  );

  function handleFromChange(event: ChangeEvent<HTMLInputElement>) {
    const trimmed = event.target.value.trim();

    setFromName(trimmed);
    emailFromHelpers.setValue(`${trimmed}@${sending_domain}`);
  }

  function handleReplyToChange(event: ChangeEvent<HTMLInputElement>) {
    const trimmed = event.target.value.trim();

    setReplyToName(trimmed);
    emailReplyToHelpers.setValue(trimmed);
  }

  return (
    <Fragment>
      <Grid container alignItems="center">
        <Grid item xs={3}>
          <Typography variant="subtitle2">{t('contentEmails:fromAddress')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <TextField
            fullWidth
            error={!!emailFromMeta.error && !!emailFromMeta.touched}
            value={fromName}
            margin="none"
            placeholder={t('contentEmails:fromAddress')}
            InputProps={{ endAdornment }}
            onChange={handleFromChange}
            {...fromErrorText}
          />
        </Grid>
      </Grid>
      <Grid container className={classes.spacer} alignItems="center">
        <Grid item xs={3}>
          <Typography variant="subtitle2">{t('contentEmails:replyTo')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <TextField
            fullWidth
            error={!!emailReplyToMeta.error && !!emailReplyToMeta.touched}
            value={replyToName}
            margin="none"
            placeholder={t('contentEmails:replyToPlaceholder')}
            onChange={handleReplyToChange}
            {...replyToErrorText}
          />
        </Grid>
      </Grid>
    </Fragment>
  );
};

FunctionTemplate.defaultProps = {
  edit: false,
};

export default FunctionTemplate;
